/**
 * Permissions API - Manejo de permisos de usuario
 *
 * Backend (Lambda + DynamoDB): para que los permisos se guarden, el Lambda debe persistir
 * en el item del usuario al menos: user, additionalCodes (lista), admin (bool), sendTasks (bool),
 * perBoardPermissions (string JSON). perBoardPermissions se envía como JSON.stringify(objeto).
 */

import { callApi, apiCache, apiCallCounter } from '../apiClient';
import { normalizeUserEmail } from '../config';
import { logger } from '../logger';
import { BOARD_PERMISSION_KEYS, getDefaultBoardPermissions } from '../permissionKeys.js';

function mergeBoardPermissions(target, source) {
  if (!source || typeof source !== 'object') return target;
  const result = { ...target };
  BOARD_PERMISSION_KEYS.forEach(key => {
    if (source[key] !== undefined) result[key] = !!source[key];
  });
  return result;
}

/**
 * Fetches user permissions from the API
 * @param {string} userEmail - The user's email address
 * @returns {Promise<Object>} - Object containing admin status and additionalCodes
 */
export async function fetchUserPermissions(userEmail) {
  try {
    // Check cache first
    if (apiCache.permissions) {
      logger.info('Using cached permissions for user:', userEmail);
      return apiCache.permissions;
    }
    
    apiCallCounter.fetchPermissions++;
    
    const normalizedEmail = normalizeUserEmail(userEmail);
    if (!normalizedEmail) {
      throw new Error('Se requiere un email de usuario válido para consultar permisos');
    }
    
    logger.info('Consultando permisos para usuario:', normalizedEmail);
    
    // Format the request body according to the API requirements
    // Important: The API expects 'user' as the parameter name, not 'email'
    const requestBody = { user: normalizedEmail };
    
    const data = await callApi('/permisos/read', requestBody);
    
    logger.info('Respuesta de permisos recibida:', data);
    
    // Parse the response - the lambda now returns data directly, not nested in body
    const defaultBoardPerms = getDefaultBoardPermissions();
    let permissions = { admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} };
    
    function parsePerBoardPermissions(raw) {
      if (!raw || typeof raw !== 'object') return {};
      const out = {};
      const codes = Object.keys(raw);
      codes.forEach(code => {
        const p = raw[code];
        if (p && typeof p === 'object') {
          out[code] = { ...defaultBoardPerms, ...mergeBoardPermissions(defaultBoardPerms, p) };
        }
      });
      return out;
    }

    function buildFromSource(src) {
      const perBoard = src.perBoardPermissions != null
        ? parsePerBoardPermissions(typeof src.perBoardPermissions === 'string' ? (() => { try { return JSON.parse(src.perBoardPermissions); } catch { return {}; } })() : src.perBoardPermissions)
        : {};
      const codes = Array.isArray(src.additionalCodes) ? src.additionalCodes : [];
      // Compatibilidad: si vienen permisos planos y no hay perBoardPermissions, aplicar esos valores a todos los códigos
      const flatPerms = mergeBoardPermissions(defaultBoardPerms, src);
      const hasFlat = BOARD_PERMISSION_KEYS.some(k => src[k] !== undefined);
      if (hasFlat && Object.keys(perBoard).length === 0 && codes.length > 0) {
        codes.forEach(code => { perBoard[code] = { ...flatPerms }; });
      }
      return {
        admin: !!src.admin,
        additionalCodes: codes,
        sendTasks: !!src.sendTasks,
        perBoardPermissions: perBoard
      };
    }

    if (data && typeof data === 'object' && (data.admin !== undefined || data.additionalCodes !== undefined || data.sendTasks !== undefined)) {
      permissions = buildFromSource(data);
    } else if (data && data.body && typeof data.body === 'string') {
      try {
        const parsedBody = JSON.parse(data.body);
        permissions = buildFromSource(parsedBody);
      } catch (e) {
        logger.error('Error parsing permissions response body:', e);
      }
    }
    
    apiCache.permissions = permissions;
    return permissions;
  } catch (error) {
    logger.error('Error fetching user permissions:', error);
    return { admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} };
  }
}

/**
 * Fetches users who have access to a specific board code from the API
 * @param {string} boardCode - The board code to filter by
 * @returns {Promise<Array<string>>} - An array of user emails that have access to the board
 */
export async function fetchUsersByBoardCode(boardCode) {
  if (!boardCode) {
    logger.warn('fetchUsersByBoardCode called without a boardCode');
    return [];
  }
  
  try {
    apiCallCounter.fetchUsersByCode = (apiCallCounter.fetchUsersByCode || 0) + 1;

    // The endpoint is GET /permisos/users-by-code?code=THE_CODE
    // We need to use a GET request with query parameters
    const data = await callApi(`/permisos/users-by-code?code=${encodeURIComponent(boardCode)}`, null, 'GET');

    // The response is expected to be an array of objects: [{ user: 'email1' }, { user: 'email2' }]
    if (Array.isArray(data) && data.length > 0) {
      const userEmails = data.map(item => item.user).filter(Boolean);
      logger.info(`Found ${userEmails.length} users for board code ${boardCode}:`, userEmails);
      return userEmails;
    }

    // If the response is an empty array, it means no users were found, which is a valid case.
    logger.info(`No users found for board code ${boardCode}`);
    return [];
  } catch (error) {
    logger.error(`Error fetching users for board code ${boardCode}:`, error);
    // Return an empty array on error to prevent the UI from crashing.
    return [];
  }
}

/**
 * Fetches all admin users from the API
 * @returns {Promise<Array<string>>} - An array of user emails that have admin permissions
 */
export async function fetchAdminUsers() {
  try {
    apiCallCounter.fetchAdminUsers = (apiCallCounter.fetchAdminUsers || 0) + 1;

    // The endpoint is GET /permisos/admin-list
    // We need to use a GET request
    const data = await callApi('/permisos/admin-list', null, 'GET');

    logger.info('Raw admin-list response:', data);

    // The response can be in different formats:
    // 1. Direct array: [{ user: 'email1' }, { user: 'email2' }] or ['email1', 'email2']
    // 2. Object with 'admins' property: { admins: [{ user: 'email1' }, ...], count: 2 }
    let adminsArray = null;

    // Check if data is directly an array
    if (Array.isArray(data)) {
      adminsArray = data;
    }
    // Check if data has an 'admins' property
    else if (data && data.admins && Array.isArray(data.admins)) {
      adminsArray = data.admins;
    }
    // Check if data has a 'body' property that might contain the array
    else if (data && data.body) {
      try {
        const parsedBody = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        if (Array.isArray(parsedBody)) {
          adminsArray = parsedBody;
        } else if (parsedBody && Array.isArray(parsedBody.admins)) {
          adminsArray = parsedBody.admins;
        }
      } catch (e) {
        logger.error('Error parsing admin list response body:', e);
      }
    }

    // Process the admins array if we found one
    if (adminsArray && adminsArray.length > 0) {
      logger.info('Processing admins array:', adminsArray);
      // Handle both formats: array of objects with 'user' property or array of strings
      const userEmails = adminsArray.map(item => {
        if (typeof item === 'string') {
          return item;
        }
        const email = item.user || item.email || null;
        logger.info('Extracting email from item:', item, '->', email);
        return email;
      }).filter(Boolean);
      
      logger.info(`Found ${userEmails.length} admin users:`, userEmails);
      return userEmails;
    }

    // If no admins were found, it's a valid case.
    logger.info('No admin users found');
    return [];
  } catch (error) {
    logger.error('Error fetching admin users:', error);
    // Return an empty array on error to prevent the UI from crashing.
    return [];
  }
}

/**
 * Edits user permissions in the system
 * @param {string} userEmail - The user's email address
 * @param {Object} updates - Permission updates object containing admin, additionalCodes, and sendTasks
 * @param {Array} previousCodes - Array of previous codes the user had before the update
 * @returns {Promise<Object>} - The response from the API with added operationType
 */
export async function editUserPermissions(userEmail, updates, previousCodes = null) {
  try {
    if (!userEmail) {
      throw new Error('Se requiere un email de usuario válido para editar permisos');
    }
    
    logger.important(`API Call - editUserPermissions para usuario: ${userEmail}`);
    
    // Strategy: Try UPDATE first, if it fails because user doesn't exist, then CREATE
    // This is more reliable than trying to detect user existence beforehand
    
    try {
      // First, try to get current codes if not provided
      let currentCodes = [];
      
      if (previousCodes === null) {
        // Try to get current permissions to compare codes
        try {
          const readRequestBody = { user: userEmail };
          const readData = await callApi('/permisos/read', readRequestBody);
          
          // Extract current codes from response
          if (readData && typeof readData === 'object') {
            if (Array.isArray(readData.additionalCodes)) {
              currentCodes = readData.additionalCodes;
            } else if (readData.body) {
              try {
                const parsedBody = typeof readData.body === 'string' ? JSON.parse(readData.body) : readData.body;
                if (Array.isArray(parsedBody.additionalCodes)) {
                  currentCodes = parsedBody.additionalCodes;
                } else if (Array.isArray(parsedBody['additional-codes'])) {
                  currentCodes = parsedBody['additional-codes'];
                }
              } catch (e) {
                // Ignore parsing errors for codes
              }
            }
          }
        } catch (readError) {
          // If we can't read current codes, proceed with empty array
          currentCodes = [];
        }
      }
      
      // Use provided previousCodes if available, otherwise use currentCodes from the API
      const existingCodes = previousCodes !== null ? previousCodes : currentCodes;
      const newCodes = updates.additionalCodes || [];
      
      // Find codes that were removed
      const deletedCodes = existingCodes.filter(code => !newCodes.includes(code));
      
      // If we have deleted codes, we need to call the delete endpoint first
      if (deletedCodes.length > 0) {
        const deleteRequestBody = {
          user: userEmail,
          updates: {
            deleteCodes: deletedCodes
          }
        };
        await callApi('/permisos/delete', deleteRequestBody);
      }
      
      const updatesObject = {
        additionalCodes: newCodes,
        admin: updates.admin !== undefined ? updates.admin : false,
        sendTasks: updates.sendTasks !== undefined ? updates.sendTasks : false
      };
      if (updates.perBoardPermissions !== undefined && typeof updates.perBoardPermissions === 'object' && Object.keys(updates.perBoardPermissions).length > 0) {
        updatesObject.perBoardPermissions = JSON.stringify(updates.perBoardPermissions);
      }
      
      const updateRequestBody = {
        user: userEmail,
        updates: updatesObject
      };
      
      const updateData = await callApi('/permisos/update', updateRequestBody);
      
      // Clear permissions cache to ensure fresh data
      apiCache.permissions = null;
      
      // Agregar información sobre la operación
      updateData.operationType = 'update';
      updateData.deletedCodes = deletedCodes;
      
      return updateData;
      
    } catch (updateError) {
      // Check if the error indicates that the user doesn't exist
      const errorMessage = updateError.message || '';
      const errorString = String(updateError);
      
      // Check multiple possible indicators that the user doesn't exist
      const userNotFoundIndicators = [
        '404',
        'not found',
        'no encontrado',
        'Usuario no encontrado',
        'user not found',
        'User not found',
        'No se encontró el usuario',
        'No se encontro el usuario'
      ];
      
      const isUserNotFound = userNotFoundIndicators.some(indicator => 
        errorMessage.toLowerCase().includes(indicator.toLowerCase()) ||
        errorString.toLowerCase().includes(indicator.toLowerCase())
      );
      
      if (isUserNotFound) {
        // This is expected - user doesn't exist, so we'll create them
        // Don't log this as an error since it's handled gracefully
        logger.info(`Usuario ${userEmail} no encontrado, creando nuevo registro de permisos`);
        
        const createRequestBody = {
          user: userEmail,
          additionalCodes: updates.additionalCodes || [],
          admin: updates.admin !== undefined ? updates.admin : false,
          sendTasks: updates.sendTasks !== undefined ? updates.sendTasks : false
        };
        if (updates.perBoardPermissions !== undefined && typeof updates.perBoardPermissions === 'object' && Object.keys(updates.perBoardPermissions).length > 0) {
          createRequestBody.perBoardPermissions = JSON.stringify(updates.perBoardPermissions);
        }
        
        try {
          const createData = await callApi('/permisos/create', createRequestBody);
          
          // Clear permissions cache to ensure fresh data
          apiCache.permissions = null;
          
          // Agregar el tipo de operación realizada
          createData.operationType = 'create';
          
          return createData;
        } catch (createError) {
          // If CREATE also fails, then it's a real error
          logger.error(`Error al crear permisos para ${userEmail}:`, createError);
          throw createError;
        }
      } else {
        // If it's not a "user not found" error, re-throw the original error
        throw updateError;
      }
    }
    
  } catch (error) {
    logger.error('Error editing user permissions:', error);
    throw error;
  }
} 